import { AnyAction, createReducer, nanoid } from '@reduxjs/toolkit';

import { ErrorCodes } from '@src/types/errors';

import { accountsActions } from '@modules/accounts';
import { cardsActions } from '@modules/cards';
import { walletActions } from '@modules/wallet';

import notificationsActions from './actions';
import initialState, { NotificationsStateType } from './state';
import * as NotificationsTypes from './types';
import { buyActions } from '../buy';
import { loginActions } from '../login';
import { userActions } from '../user';
import { withdrawActions } from '../withdraw';

const { error, notification } = NotificationsTypes.NotificationTypesEnum;

const IGNORE_ACTIONS_LIST = [
  loginActions.setPassword,
  loginActions.resendVerification,
  loginActions.keepAlive,
  userActions.getUserData,
  userActions.changeNewPassword,
  withdrawActions.withdrawVerifyEmail,
  withdrawActions.getWithdrawRate,
  withdrawActions.getWithdrawEstimate,
  walletActions.getRateForRateInformer,
  withdrawActions.withdrawResendVerification,
  accountsActions.accountWithdrawVerifyResend,
  buyActions.getBuyRate,
  cardsActions.bind,
];

const IGNORE_CAPTCHA_ACTIONS = [
  loginActions.resendVerification,
  loginActions.resetPasswordPhone,
  userActions.changePassword,
  userActions.changePhone,
  userActions.changeEmailSendOldEmail,
  userActions.changeEmailSendNewEmail,
  accountsActions.accountWithdraw,
  withdrawActions.withdrawResendVerification,
  accountsActions.accountWithdrawVerifyResend,
];

const IGNORE_ERROR_CODES: ErrorCodes[] = [
  CONST.errorCodes.userBlocked,
  CONST.errorCodes.ERR_NETWORK,
];

const filter = (action: AnyAction) => {
  const isIgnoreActions =
    !IGNORE_ACTIONS_LIST?.map(act => act.rejected.type)?.includes(action.type) &&
    !IGNORE_ERROR_CODES.includes(action?.payload?.code);

  const isIgnoreCaptchaActions =
    (action.type === loginActions.login.rejected.type &&
      action?.payload?.code !== CONST.errorCodes.captchaCode) ||
    (!IGNORE_CAPTCHA_ACTIONS?.map(act => act.rejected.type)?.includes(action.type) &&
      action?.payload?.code !== CONST.errorCodes.captchaCode);

  return action.type.endsWith('/rejected') && isIgnoreActions && isIgnoreCaptchaActions;
};

const addNotification = (state: NotificationsStateType, action: AnyAction, type = notification) => {
  if (action.payload?.message) {
    const title = action.payload.title || action.payload.name;

    const SPECIFIC_ERROR_MESSAGE = action.payload.data?.card_id?.[0];
    const message = SPECIFIC_ERROR_MESSAGE || action.payload.message;

    const { code } = action.payload;

    state.list = [{ id: nanoid(), type, title, message, code }];
  }
};

export const notificationsReducer = createReducer(initialState, builder => {
  builder.addCase(notificationsActions.add, (state, action) =>
    addNotification(state, action, action.payload.type)
  );

  builder.addCase(notificationsActions.remove, (state, action) => {
    state.list = state.list.filter(item => item.id !== action.payload);
  });

  builder.addCase(notificationsActions.clear, state => {
    state.list = [];
  });

  builder.addMatcher(filter, (state, action) => addNotification(state, action, error));
});

export { notificationsActions, NotificationsTypes };
